import { useEffect, useState } from 'react'
import { useManualQuery } from 'graphql-hooks'

import { getConfigureUsersData } from '../../../../graphql/custom-queries'

import { IGetTeamsShort } from '../../../../types/teams'
import { IGetLocationsShort } from '../../../../types/locations'
import { IGetConfigureUsersData, IGetLeaveTypeList } from '../../../../types/custom-queries'

interface IUsePlatformUsersResult {
  departments: IGetTeamsShort[]
  locations: IGetLocationsShort[]
  leaveTypes: IGetLeaveTypeList[]
  defaultDepartmentId: string
  defaultLocationId: string
  companyShouldProrate: boolean
  companyProratedLeaveTypes: string[]
  isLoading: boolean
}

export const usePlatformUsers = (): IUsePlatformUsersResult => {
  const [departments, setDepartments] = useState<IGetTeamsShort[]>([])
  const [locations, setLocations] = useState<IGetLocationsShort[]>([])
  const [leaveTypes, setLeaveTypes] = useState<IGetLeaveTypeList[]>([])
  const [defaultDepartmentId, setDefaultDepartmentId] = useState('')
  const [defaultLocationId, setDefaultLocationId] = useState('')
  const [companyShouldProrate, setCompanyShouldProrate] = useState(false)
  const [companyProratedLeaveTypes, setCompanyProratedLeaveTypes] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const [getConfigureUsersDataQuery] = useManualQuery<IGetConfigureUsersData>(getConfigureUsersData)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getConfigureUsersDataQuery()
        if (!response.data || response.error) {
          setIsLoading(false)
          return
        }

        const teamList = response.data.getTeamList || []
        const locationList = response.data.getLocationList || []
        const leaveTypeList = response.data.getLeaveTypeList || []

        setDepartments(teamList)
        setLocations(locationList)
        setLeaveTypes(leaveTypeList)

        const company = response.data.getCompany
        setCompanyShouldProrate(Boolean(company?.shouldProrate))
        setCompanyProratedLeaveTypes(company?.proratedLeaveTypes || [])

        const defaultDepartmentForAutomaticImport = company?.defaultDepartmentForAutomaticImport
        const defaultLocationForAutomaticImport = company?.defaultLocationForAutomaticImport

        // Find default department: use company default or fall back to isDefault
        const foundDepartment = defaultDepartmentForAutomaticImport ? teamList.find((d) => d.id === defaultDepartmentForAutomaticImport) : teamList.find((d) => d.isDefault)
        if (foundDepartment) {
          setDefaultDepartmentId(foundDepartment.id)
        } else if (teamList.length > 0) {
          setDefaultDepartmentId(teamList[0].id)
        }

        // Find default location: use company default or fall back to isDefault
        const foundLocation = defaultLocationForAutomaticImport ? locationList.find((l) => l.id === defaultLocationForAutomaticImport) : locationList.find((l) => l.isDefault)
        if (foundLocation) {
          setDefaultLocationId(foundLocation.id)
        } else if (locationList.length > 0) {
          setDefaultLocationId(locationList[0].id)
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  return {
    departments,
    locations,
    leaveTypes,
    defaultDepartmentId,
    defaultLocationId,
    companyShouldProrate,
    companyProratedLeaveTypes,
    isLoading,
  }
}
